/**
 * fetch method
 * @param {String} url endpoint for the call
 * @param {String} httpMethod 'GET' and 'POST'
 * @param {Object} data form-data/POST body
 * @param {Object} headers additional headers if required
 */
const fetchMethod = (url, httpMethod = 'GET', data, headers) => new Promise((resolve, reject) => {
  const time = new Date().getTime();
  let endpoint = url;
  const updatedData = data ? Object.assign({}, data) : null;
  if (httpMethod === 'GET') {
    if (url.includes('?')) {
      endpoint = `${url}&t=${time}`;
    } else {
      endpoint = `${url}?t=${time}`;
    }
  } else if (httpMethod === 'POST') {
    updatedData.t = time;
  }
  const xhttp = new XMLHttpRequest();
  xhttp.onreadystatechange = () => {
    const {
      responseText
    } = xhttp;
    if (xhttp.readyState === 4 && [200, 204].indexOf(xhttp.status) > -1) {
      resolve(JSON.parse(responseText));
    } else if (xhttp.readyState === 4) {
      reject(responseText);
    }
  };
  xhttp.open(httpMethod, endpoint, true);
  if (headers) {
    for (let key in headers) {
      // eslint-disable-line
      const val = headers[key];
      xhttp.setRequestHeader(key, val);
    }
  }
  xhttp.setRequestHeader('Accept', 'application/json');
  xhttp.setRequestHeader('Content-Type', 'application/json');
  xhttp.withCredentials = true;
  xhttp.send(data ? JSON.stringify(updatedData) : null);
});

/**
 * GET call
 * @param {String} url to do the get call
 */
function getData(url, headers) {
  const promise = fetchMethod(url, 'GET', null, headers);
  return promise;
}

/**
 * POST call
 * @param {String} url endpoint for POST
 * @param {Object} data form-data/POST body
 * @param {Object} headers additional headers if required
 * @returns {Promise} promise of the post call
 */
function postData(url, data, headers) {
  const promise = fetchMethod(url, 'POST', data, headers);
  return promise;
}
var fetchHelper = {
  getData,
  postData
};

/* global fetchHelper, spaParams */

// endpoints
const RETRIEVE_DESKTOP = '/myaccounts/omni/shoppingcart/v1/retrieve';
const RETRIEVE_MDA = '/mgateway/myaccounts-ws/omni/shoppingcart/v1/retrieve';
const GET_DESKTOP = '/myaccounts/target/services/common/v2/customer?client=';
const GET_MDA = '/mgateway/myaccounts-ws/target/services/common/v2/customer?client=';
const GET_SIGNOFF_DESKTOP = '/myaccounts/omni/v2/signoff';
const GET_SIGNOFF_MDA = '/mgateway/myaccounts-ws/omni/v2/signoff';
const CART_OP_DESKTOP = '/myaccounts/omni/shoppingcart/v1/update?retrieveShoppingCart=true';
const CART_OP_MDA = '/mgateway/myaccounts-ws/omni/shoppingcart/v1/update?retrieveShoppingCart=true';
const BARKER_COUNT_DESKTOP = '/myaccounts/omni/shoppingcart/v1/retrieveShoppingCartCount';
const BARKER_COUNT_MDA = '/mgateway/myaccounts-ws/omni/shoppingcart/v1/retrieveShoppingCartCount';

// constant ENUMS
const ADD_OPERATION = 'Add';
const REMOVE_OPERATION = 'Remove';
const PIPAD_DESKTOP = spaParams.siteParams.secureHost;
const PIPAD_MDA = spaParams.siteParams.mdaHost;

/**
 * @function isMDA Determines whether we're in MDA mode.
 * @description MDA sends 'ese=true' in query params to help us determine
 *                    whether the page is loaded within MDA environment
 * @description When in MDA mode, we need to make PIPAD calls to PIPAD_MDA rather than PIPAD_DESKTOP
 * @description This function spartaGlobalFunctions.getUrlParam to get wrap value and
 *                    adds a check for a trailing '/' for edge case URL's
 * @returns {Boolean} Whether we're in MDA mode, true or not, false.
 */
function isMDA() {
  const {
    href
  } = window.location;
  const hasESETrue = /ese=true/i.test(href);
  const localStorageValue = window.localStorage ? window.localStorage.getItem('isMDA') === 'true' : null;
  const sessionStorageValue = window.sessionStorage ? window.sessionStorage.getItem('mda') === 'true' : null;
  if (localStorageValue || sessionStorageValue || hasESETrue) {
    return true;
  }
  return false;
}

/**
 * get call to get barker count
 * @returns {Promise} promise of get barker count call
 */
function getBarkerCount() {
  let endpoint = `${PIPAD_DESKTOP}${BARKER_COUNT_DESKTOP}`;
  const mda = isMDA();
  if (mda) {
    endpoint = `${PIPAD_MDA}${BARKER_COUNT_MDA}`;
  }
  const promise = fetchHelper.getData(endpoint, {});
  return promise;
}

/**
 * get call to sign off
 * @return {Promise} promise of get call to sign off
 */
function getSignOff() {
  let endpoint = `${PIPAD_DESKTOP}${GET_SIGNOFF_DESKTOP}`;
  const mda = isMDA();
  if (mda) {
    endpoint = `${PIPAD_DESKTOP}${GET_SIGNOFF_MDA}`;
  }
  const promise = fetchHelper.getData(endpoint, {});
  return promise;
}

/**
 * get call to get the products
 * @param {String} client PIPAD client to get the products
 * @returns {Promise} promise of get cart call
 */
function get(client) {
  let endpoint = `${PIPAD_DESKTOP}${GET_DESKTOP}${client}`;
  const mda = isMDA();
  if (mda) {
    endpoint = `${PIPAD_MDA}${GET_MDA}${client}`;
  }
  const promise = fetchHelper.getData(endpoint, {});
  return promise;
}

/**
 * PIPAD post call to add item to cart
 * @param {CartItemObject} cartItem product/application to add to the cart
 * @returns {Promise} promise of add call
 */
function add(cartItem) {
  const item = {
    ...cartItem,
    operation: ADD_OPERATION
  };
  let endpoint = `${PIPAD_DESKTOP}${CART_OP_DESKTOP}`;
  const mda = isMDA();
  if (mda) {
    endpoint = `${PIPAD_MDA}${CART_OP_MDA}`;
  }
  const promise = fetchHelper.postData(endpoint, item, {});
  return promise;
}

/**
 * PIPAD post call to remove item from cart
 * @param {CartItemObject} cartItem product/application to remove to the cart
 * @returns {Promise} promise of remove call
 */
function remove(cartItem) {
  const item = {
    ...cartItem,
    operation: REMOVE_OPERATION
  };
  let endpoint = `${PIPAD_DESKTOP}${CART_OP_DESKTOP}`;
  const mda = isMDA();
  if (mda) {
    endpoint = `${PIPAD_MDA}${CART_OP_MDA}`;
  }
  const promise = fetchHelper.postData(endpoint, item, {});
  return promise;
}

/**
 * Retrieve the complete shopping cart
 */
function retrieve() {
  let endpoint = `${PIPAD_DESKTOP}${RETRIEVE_DESKTOP}`;
  const mda = isMDA();
  if (mda) {
    endpoint = `${PIPAD_MDA}${RETRIEVE_MDA}`;
  }
  const promise = fetchHelper.getData(endpoint, {});
  return promise;
}
var index = {
  get,
  add,
  remove,
  getBarkerCount,
  retrieve,
  getSignOff
};

/**
 * @typedef {Object} CartItemObject the list of all the labels
 * @property {String} operation operation to perform // Add or Remove
 * @property {String} cartItemType type of item // Product or Application
 * @property {Object} product product details
 */

export { index as default };
